import SafeSummaryTile from "../../../components/common/SafeSummaryTile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import {
  EmployeeBulkPicker,
  EmployeeSelect,
} from "../../../components/common/EmployeeSelect";
import { EmptyState } from "../../../components/common/EmptyState";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";
import { SafePageHeader } from "../../../components/common/HelperFunc";

const STATUS_OPTIONS = [
  {
    value: "completed",
    label: "완료",
    color: "success",
    icon: <CheckCircleIcon />,
  },
  { value: "missed", label: "미실시", color: "error", icon: <EventBusyIcon /> },
  {
    value: "unregistered",
    label: "미등록",
    color: "default",
    icon: <CalendarMonthIcon />,
  },
];

const TBM_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="tbm-header"]',
    title: "TBM 일일 관리",
    description:
      "조회 일자를 기준으로 팀/라인별 TBM 실시 상태를 확인합니다. 안내 과정에서는 실제 TBM 데이터가 저장되거나 변경되지 않습니다.",
  },
  {
    target: '[data-autopilot="tbm-actions"]',
    title: "조회와 등록",
    description:
      "날짜를 바꾸면 해당 일자의 TBM 목록을 조회합니다. 등록 버튼에서는 진행자, 참석자, 체크리스트, 현장 사진을 입력합니다.",
  },
  {
    target: '[data-autopilot="tbm-summary"]',
    title: "실시 현황 요약",
    description:
      "완료, 미실시, 미등록, 전체 대상 인원과 참석률을 요약합니다. 누락된 라인과 참석률이 낮은 팀을 빠르게 파악할 수 있습니다.",
  },
];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDate = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthBounds = (dateText = getToday()) => {
  const [year, month] = String(dateText).split("-").map(Number);
  const base =
    Number.isFinite(year) && Number.isFinite(month)
      ? new Date(year, month - 1, 1)
      : new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return {
    start: formatDate(start),
    end: formatDate(end),
    label: `${start.getFullYear()}년 ${start.getMonth() + 1}월`,
  };
};

const getShiftedMonthDate = (dateText = getToday(), offset = 0) => {
  const [year, month] = String(dateText).split("-").map(Number);
  const base =
    Number.isFinite(year) && Number.isFinite(month)
      ? new Date(year, month - 1, 1)
      : new Date();
  return formatDate(new Date(base.getFullYear(), base.getMonth() + offset, 1));
};

const getDefaultDateTime = (date = getToday()) => `${date}T08:00`;

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[2];

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const makeEmptyForm = (date, options = {}) => ({
  remarks: '',
  location_id: "",
  department_id: "",
  team_name: "",
  leader_emp_id: "",
  session_date: getDefaultDateTime(date),
  status: "completed",
  total_members: 0,
  attendees: [],
  checklist: (options.checklistTemplates || []).map((item) => ({
    template_id: Number(item.id),
    is_checked: false,
  })),
});

const buildTeamCreateForm = (date, options = {}, team = {}) => {
  const base = makeEmptyForm(date, options);
  const department = (options.departments || []).find(
    (item) => String(item.department_id) === String(team.department_id)
  );
  const departmentId = department?.department_id || team.department_id || "";
  const teamName = department?.department_name || team.team_name || "";
  const teamEmployees = departmentId
    ? (options.employees || []).filter(
        (employee) =>
          String(employee.department_id || "") === String(departmentId)
      )
    : [];

  return {
    ...base,
    department_id: departmentId ? String(departmentId) : "",
    team_name: teamName,
    session_date: team.session_date_input || getDefaultDateTime(date),
    status: "completed",
    total_members: teamEmployees.length || team.total_members || 0,
    attendees: teamEmployees.map((employee) => ({
      emp_id: Number(employee.emp_id),
      is_attended: true,
    })),
  };
};

const normalizeBool = (value) => value === true || value === 1 || value === "1";

const buildEditForm = (session, options = {}, date = getToday()) => {
  const checklistMap = new Map(
    (session.checklist || []).map((item) => [
      Number(item.template_id),
      normalizeBool(item.is_checked),
    ])
  );
  const matchedDepartment = (options.departments || []).find(
    (department) =>
      String(department.department_name || "") ===
      String(session.team_name || "")
  );

  return {
    location_id: session.location_id || "",
    remarks: session.remarks || '',
    department_id: matchedDepartment?.department_id
      ? String(matchedDepartment.department_id)
      : "",
    team_name: session.team_name || "",
    leader_emp_id: session.leader_emp_id ? String(session.leader_emp_id) : "",
    session_date: session.session_date_input || getDefaultDateTime(date),
    status: session.status || "unregistered",
    total_members: session.total_members || 0,
    attendees: (session.attendees || []).map((item) => ({
      emp_id: Number(item.emp_id),
      is_attended: normalizeBool(item.is_attended),
    })),
    checklist: (options.checklistTemplates || []).map((item) => ({
      template_id: Number(item.id),
      is_checked: checklistMap.get(Number(item.id)) || false,
    })),
  };
};

const buildFormData = (form, photoFile) => {
  const attendees = form.attendees || [];
  const formData = new FormData();

  formData.append("location_id", form.location_id || "");
  formData.append('remarks', form.remarks || '');
  formData.append("team_name", form.team_name || "");
  formData.append("leader_emp_id", form.leader_emp_id || "");
  formData.append("session_date", form.session_date || "");
  formData.append("status", form.status || "unregistered");
  formData.append("total_members", Number(attendees.length || 0));
  formData.append(
    "attendees",
    JSON.stringify(
      attendees.map((item) => ({
        emp_id: Number(item.emp_id),
        is_attended: Boolean(item.is_attended),
      }))
    )
  );
  formData.append(
    "checklist",
    JSON.stringify(
      (form.checklist || []).map((item) => ({
        template_id: Number(item.template_id),
        is_checked: Boolean(item.is_checked),
      }))
    )
  );

  if (photoFile) {
    formData.append("photo", photoFile);
  }

  return formData;
};

const TILE_COLORS = {
  primary: { main: "#1976d2", bg: "#e8f2ff" },
  success: { main: "#2e7d32", bg: "#e8f5e9" },
  warning: { main: "#ed6c02", bg: "#fff3e0" },
  error: { main: "#d32f2f", bg: "#ffebee" },
};

const MONTH_STATUS_META = {
  completed: {
    label: "완료",
    color: "#16a34a",
    bg: "#ecfdf5",
    border: "#bbf7d0",
  },
  missed: {
    label: "미실시",
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  unregistered: {
    label: "미등록",
    color: "#d97706",
    bg: "#fff7ed",
    border: "#fed7aa",
  },
};

const getMonthTbmStatusSummary = (day = {}) => {
  const completed = Number(day.completed || 0);
  const missed = Number(day.missed || 0);
  const unregistered = Number(day.unregistered || 0);
  const total = Number(day.total || completed + missed + unregistered);
  const meta = MONTH_STATUS_META[day.status] || MONTH_STATUS_META.unregistered;

  if (total > 0 && completed === total && missed === 0 && unregistered === 0) {
    return {
      ...MONTH_STATUS_META.completed,
      isAllComplete: true,
      label: "전체완료",
      title: `완료 ${completed}팀`,
      items: [],
    };
  }

  return {
    ...meta,
    isAllComplete: false,
    label: "상태 집계",
    title: `완료 ${completed}팀 / 미실시 ${missed}팀 / 미등록 ${unregistered}팀`,
    items: [
      {
        key: "completed",
        label: "완료",
        count: completed,
        ...MONTH_STATUS_META.completed,
      },
      {
        key: "missed",
        label: "미실시",
        count: missed,
        ...MONTH_STATUS_META.missed,
      },
      {
        key: "unregistered",
        label: "미등록",
        count: unregistered,
        ...MONTH_STATUS_META.unregistered,
      },
    ],
  };
};

function SummaryTile(props) { return <SafeSummaryTile {...props} />; }

function TbmFormDialog({
  open,
  mode,
  form,
  employees,
  departments,
  checklistTemplates,
  photoFile,
  isMobile,
  isSaving,
  isLoadingDetail,
  onClose,
  onChange,
  onPhotoChange,
  onSubmit,
}) {
  const isEdit = mode === "edit";
  const dialogTitle = isEdit
    ? isMobile
      ? "TBM 기록 수정"
      : "작업 전 안전점검회의(TBM) 기록 수정"
    : isMobile
    ? "현장 TBM 기록"
    : "작업 전 안전점검회의(TBM) 기록 등록";
  const submitLabel = isEdit
    ? "수정 완료"
    : isMobile
    ? "기록 완료"
    : "등록 완료";

  const attendeeMap = useMemo(
    () =>
      new Map(
        (form.attendees || []).map((item) => [Number(item.emp_id), item])
      ),
    [form.attendees]
  );

  const checklistMap = useMemo(
    () =>
      new Map(
        (form.checklist || []).map((item) => [Number(item.template_id), item])
      ),
    [form.checklist]
  );

  const teamEmployees = useMemo(() => {
    if (!form.department_id) return employees;
    return employees.filter(
      (employee) =>
        String(employee.department_id || "") === String(form.department_id)
    );
  }, [employees, form.department_id]);

  const handleDepartmentChange = (value) => {
    const selectedDepartment = (departments || []).find(
      (department) => String(department.department_id) === String(value)
    );
    const nextTeamName = selectedDepartment?.department_name || "";
    const nextEmployees = value
      ? employees.filter(
          (employee) => String(employee.department_id || "") === String(value)
        )
      : [];
    const nextAttendees = nextEmployees.map((employee) => ({
      emp_id: Number(employee.emp_id),
      is_attended: true,
    }));
    const leaderStillInTeam = nextEmployees.some(
      (employee) => String(employee.emp_id) === String(form.leader_emp_id || "")
    );

    onChange("department_id", value);
    onChange("team_name", nextTeamName);
    onChange("attendees", nextAttendees);
    onChange("total_members", nextAttendees.length);
    if (!leaderStillInTeam) onChange("leader_emp_id", "");
  };

  const targetCount = form.attendees?.length || 0;
  const attendedCount = (form.attendees || []).filter(
    (item) => item.is_attended
  ).length;

  const setAttendeeTarget = (employee, checked) => {
    const empId = Number(employee.emp_id);
    if (checked) {
      onChange("attendees", [
        ...(form.attendees || []),
        {
          emp_id: empId,
          is_attended: true,
        },
      ]);
      if (!form.total_members || Number(form.total_members) < targetCount + 1) {
        onChange("total_members", targetCount + 1);
      }
      return;
    }

    const nextAttendees = (form.attendees || []).filter(
      (item) => Number(item.emp_id) !== empId
    );
    onChange("attendees", nextAttendees);
    if (Number(form.total_members) === targetCount) {
      onChange("total_members", nextAttendees.length);
    }
  };

  const setAttendeeTargets = (nextEmployees, checked) => {
    const currentAttendees = form.attendees || [];
    const currentMap = new Map(
      currentAttendees.map((item) => [Number(item.emp_id), item])
    );

    if (checked) {
      nextEmployees.forEach((employee) => {
        const empId = Number(employee.emp_id);
        if (!currentMap.has(empId))
          currentMap.set(empId, { emp_id: empId, is_attended: true });
      });
    } else {
      nextEmployees.forEach((employee) =>
        currentMap.delete(Number(employee.emp_id))
      );
    }

    const nextAttendees = Array.from(currentMap.values());
    onChange("attendees", nextAttendees);
    onChange("total_members", nextAttendees.length);
  };

  const setAttendeeStatus = (employee, checked) => {
    const empId = Number(employee.emp_id);
    onChange(
      "attendees",
      (form.attendees || []).map((item) =>
        Number(item.emp_id) === empId ? { ...item, is_attended: checked } : item
      )
    );
  };

  const setChecklistChecked = (template, checked) => {
    const templateId = Number(template.id);
    const existing = checklistMap.get(templateId);
    if (existing) {
      onChange(
        "checklist",
        (form.checklist || []).map((item) =>
          Number(item.template_id) === templateId
            ? { ...item, is_checked: checked }
            : item
        )
      );
      return;
    }
    onChange("checklist", [
      ...(form.checklist || []),
      { template_id: templateId, is_checked: checked },
    ]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 18px)", sm: 2 },
          pb: { xs: 1.5, sm: 1 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 18, sm: 20 },
                lineHeight: 1.25,
                textAlign: "left",
              }}
            >
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: "#f7f8fb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
        }}
      >
        {isLoadingDetail ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 360 }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2.25}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Typography sx={{ fontWeight: 800, mb: 2 }}>
                  기본 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>팀/라인</InputLabel>
                      <Select
                        label="팀/라인"
                        value={form.department_id || ""}
                        onChange={(event) =>
                          handleDepartmentChange(event.target.value)
                        }
                      >
                        <MenuItem value="" disabled>
                          팀/부서 선택
                        </MenuItem>
                        {!(departments || []).length ? (
                          <MenuItem value="" disabled>
                            등록된 팀/부서가 없습니다
                          </MenuItem>
                        ) : null}
                        {(departments || []).map((department) => (
                          <MenuItem
                            key={department.department_id}
                            value={String(department.department_id)}
                          >
                            {department.department_name}
                            {department.member_count
                              ? ` / ${department.member_count}명`
                              : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <EmployeeSelect
                      employees={teamEmployees}
                      value={form.leader_emp_id || ""}
                      onChange={(value) => onChange("leader_emp_id", value)}
                      label="진행자"
                      required
                      teamFilter={false}
                      helperText="선택한 팀의 근로자만 표시됩니다."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="실시 일시"
                      value={form.session_date}
                      onChange={(event) =>
                        onChange("session_date", event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>상태</InputLabel>
                      <Select
                        label="상태"
                        value={form.status}
                        onChange={(event) =>
                          onChange("status", event.target.value)
                        }
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <ImageUploadBox
                      label="현장 사진"
                      file={photoFile}
                      onFileChange={onPhotoChange}
                      compact
                      sx={{
                        "& > .MuiStack-root:first-of-type": {
                          minHeight: 76,
                          py: 1.25,
                        },
                        "& img": {
                          height: 96,
                        },
                        "& > .MuiStack-root:last-of-type": {
                          p: 1.25,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <EmployeeBulkPicker
                  employees={teamEmployees}
                  selectedIds={(form.attendees || []).map(
                    (item) => item.emp_id
                  )}
                  title="참석자"
                  selectedLabel="대상"
                  scopeLabel={form.department_id ? "현재 팀" : undefined}
                  helperText={
                    form.department_id
                      ? "선택한 팀 안에서 이름·사번으로 찾거나, 현재 목록 전체를 참석 대상으로 추가할 수 있습니다."
                      : "팀/라인을 먼저 선택하면 해당 팀 근로자만 표시됩니다."
                  }
                  teamFilter={!form.department_id}
                  listHeight={{ xs: 340, md: 440 }}
                  hideSelectedCount
                  onSelectEmployee={setAttendeeTarget}
                  onSelectEmployees={(nextEmployees) =>
                    setAttendeeTargets(nextEmployees, true)
                  }
                  onDeselectEmployees={(nextEmployees) =>
                    setAttendeeTargets(nextEmployees, false)
                  }
                  renderSecondaryAction={(employee, selected) => {
                    const attendee = attendeeMap.get(Number(employee.emp_id));
                    return (
                      <FormControlLabel
                        sx={{
                          m: 0,
                          mr: -0.5,
                          whiteSpace: "nowrap",
                          "& .MuiFormControlLabel-label": {
                            fontSize: 12,
                            fontWeight: 800,
                          },
                        }}
                        control={
                          <Switch
                            edge="end"
                            checked={Boolean(attendee?.is_attended)}
                            disabled={!selected}
                            onChange={(event) =>
                              setAttendeeStatus(employee, event.target.checked)
                            }
                          />
                        }
                        label={attendee?.is_attended ? "참석" : "불참"}
                        labelPlacement="start"
                      />
                    );
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                  체크리스트
                </Typography>
                <Stack spacing={1}>
                  {checklistTemplates.map((template) => {
                    const item = checklistMap.get(Number(template.id));
                    return (
                      <Paper
                        key={template.id}
                        variant="outlined"
                        sx={{ px: 1.5, py: 1, bgcolor: "#fff" }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(item?.is_checked)}
                              onChange={(event) =>
                                setChecklistChecked(
                                  template,
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label={template.title}
                          sx={{ m: 0, width: "100%" }}
                        />
                      </Paper>
                    );
                  })}
                  {!checklistTemplates.length ? (
                    <Alert severity="info">
                      활성 체크리스트 항목이 없습니다.
                    </Alert>
                  ) : null}
                  <TextField label="비고 / 특이사항" multiline minRows={3} fullWidth
                    value={form.remarks || ''} inputProps={{ maxLength: 10000 }}
                    onChange={(event) => onChange('remarks', event.target.value)} />
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mt: 2,
                  border: "1px solid",
                  borderColor: "#dbeafe",
                  borderRadius: 2,
                  bgcolor: "#f8fbff",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1.25}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 800 }}
                    >
                      참석 대상
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 28,
                        fontWeight: 950,
                        color: "#111827",
                        lineHeight: 1,
                      }}
                    >
                      {targetCount}명
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    justifyContent={{ xs: "space-between", sm: "flex-end" }}
                    sx={{ minWidth: 0 }}
                  >
                    <Chip
                      size="small"
                      color="success"
                      label={`참석 ${attendedCount}명`}
                    />
                    <Chip
                      size="small"
                      label={`불참 ${Math.max(
                        targetCount - attendedCount,
                        0
                      )}명`}
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={
                    targetCount
                      ? Math.round((attendedCount / targetCount) * 100)
                      : 0
                  }
                  sx={{ mt: 1.25, height: 8, borderRadius: 999 }}
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                  참석률
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    targetCount
                      ? Math.round((attendedCount / targetCount) * 100)
                      : 0
                  }
                  sx={{ height: 10, borderRadius: 999 }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {attendedCount}/{targetCount}명
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {targetCount
                      ? Math.round((attendedCount / targetCount) * 100)
                      : 0}
                    %
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-end" },
          borderTop: { xs: "1px solid", sm: "none" },
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          취소
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isSaving || isLoadingDetail}
          startIcon={isSaving ? null : <PlaylistAddCheckIcon />}
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          {isSaving ? "저장 중" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MonthlyTbmCalendar({
  title,
  days,
  selectedDate,
  statusFilter = "all",
  isLoading,
  onSelectDate,
  onMonthChange,
  isMobile = false,
}) {
  const summary = useMemo(() => {
    const rows = days || [];
    return {
      completed: rows.reduce((sum, day) => sum + Number(day.completed || 0), 0),
      missed: rows.reduce((sum, day) => sum + Number(day.missed || 0), 0),
      unregistered: rows.reduce(
        (sum, day) => sum + Number(day.unregistered || 0),
        0
      ),
    };
  }, [days]);
  const leadingBlankCount = useMemo(() => {
    const firstDate = days?.[0]?.date;
    if (!firstDate) return 0;
    return new Date(`${firstDate}T00:00:00`).getDay();
  }, [days]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) auto minmax(0, 1fr)" },
          alignItems: "center",
          gap: 1.25,
          mb: 1.75,
        }}
      >
        <Box sx={{ display: { xs: "none", lg: "block" } }} />
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
          justifyContent="center"
          sx={{
            justifySelf: "center",
            minWidth: 0,
            maxWidth: "100%",
            px: 0.65,
            py: 0.55,
            border: "1px solid #e5e7eb",
            borderRadius: "999px",
            bgcolor: "#f8fafc",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Tooltip title="이전 달">
            <IconButton
              size="small"
              onClick={() => onMonthChange?.(-1)}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                border: "1px solid",
                borderColor: "#e5e7eb",
                borderRadius: "50%",
                bgcolor: "#fff",
                color: "#4f46e5",
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                "&:hover": {
                  bgcolor: "#eef2ff",
                  borderColor: "#c7d2fe",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: { xs: 0, sm: 250 },
              px: { xs: 0.4, sm: 1 },
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 18, color: "#635bff" }} />
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: { xs: 15, md: 17 },
                color: "#111827",
                letterSpacing: 0,
                lineHeight: 1.2,
                whiteSpace: { xs: "normal", sm: "nowrap" },
                textAlign: "center",
              }}
            >
              {title} TBM 현황
            </Typography>
          </Stack>
          <Tooltip title="다음 달">
            <IconButton
              size="small"
              onClick={() => onMonthChange?.(1)}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                border: "1px solid",
                borderColor: "#e5e7eb",
                borderRadius: "50%",
                bgcolor: "#fff",
                color: "#4f46e5",
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                "&:hover": {
                  bgcolor: "#eef2ff",
                  borderColor: "#c7d2fe",
                },
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          justifyContent={{ xs: "center", lg: "flex-end" }}
          sx={{ justifySelf: { xs: "center", lg: "end" }, minWidth: 0 }}
        >
          <Chip
            size="small"
            label={`완료 ${summary.completed}팀`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: "999px",
              bgcolor: "#f0fdf4",
              borderColor: "#86efac",
              color: "#15803d",
              fontWeight: 900,
              "& .MuiChip-label": { px: 1.1 },
            }}
          />
          <Chip
            size="small"
            label={`미실시 ${summary.missed}팀`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: "999px",
              bgcolor: "#fef2f2",
              borderColor: "#fecaca",
              color: "#dc2626",
              fontWeight: 900,
              "& .MuiChip-label": { px: 1.1 },
            }}
          />
        </Stack>
      </Box>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gap: { xs: 0.5, sm: 0.75 },
          }}
        >
          {["일", "월", "화", "수", "목", "금", "토"].map((label) => (
            <Typography
              key={label}
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 900 }}
            >
              {label}
            </Typography>
          ))}
          {Array.from({ length: leadingBlankCount }).map((_, index) => (
            <Box key={`blank-${index}`} />
          ))}
          {(days || []).map((day) => {
            const selectedStatusCount = statusFilter === "all"
              ? 1
              : Number(day[statusFilter] || 0);
            const matchesFilter = statusFilter === "all" || selectedStatusCount > 0;
            const meta = statusFilter === "all"
              ? MONTH_STATUS_META[day.status] || MONTH_STATUS_META.unregistered
              : MONTH_STATUS_META[statusFilter];
            const isSelected = day.date === selectedDate;
            const statusSummary = getMonthTbmStatusSummary(day);
            const visibleItems = statusFilter === "all"
              ? statusSummary.items
              : statusSummary.items.filter((item) => item.key === statusFilter);
            const showAllComplete = statusSummary.isAllComplete &&
              (statusFilter === "all" || statusFilter === "completed");
            return (
              <Box
                key={day.date}
                role={matchesFilter ? "button" : undefined}
                aria-label={`${day.date}: ${statusSummary.title}`}
                tabIndex={matchesFilter ? 0 : undefined}
                onClick={() => matchesFilter && onSelectDate(day.date)}
                onKeyDown={(event) => {
                  if (matchesFilter && (event.key === "Enter" || event.key === " "))
                    onSelectDate(day.date);
                }}
                sx={{
                  minHeight: { xs: 144, sm: 104, md: 98, xl: 102 },
                  minWidth: 0,
                  p: { xs: 0.4, sm: 0.9, md: 1.15 },
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: isSelected && matchesFilter ? "primary.main" : matchesFilter ? meta.border : "divider",
                  bgcolor: isSelected && matchesFilter ? "#eef2ff" : matchesFilter ? meta.bg : "#f8fafc",
                  cursor: matchesFilter ? "pointer" : "default",
                  opacity: matchesFilter ? 1 : 0.45,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  outline: "none",
                }}
              >
                <Typography
                  sx={{ fontWeight: 900, color: "#111827", lineHeight: 1 }}
                >
                  {Number(String(day.date).slice(8, 10))}
                </Typography>
                {showAllComplete ? (
                  <Box
                    title={statusSummary.title}
                    sx={{
                      width: "100%",
                      mt: 1,
                      px: { xs: 0, sm: 0.5, md: 1 },
                      py: 1,
                      flex: 1,
                      minHeight: { xs: 48, md: 58 },
                      borderRadius: "14px",
                      bgcolor: statusSummary.bg,
                      border: "1px solid",
                      borderColor: statusSummary.border,
                      color: statusSummary.color,
                      fontSize: { xs: 11.5, md: 14, xl: 14.5 },
                      fontWeight: 950,
                      lineHeight: 1.2,
                      textAlign: "center",
                      whiteSpace: { xs: "normal", sm: "nowrap" },
                      overflowWrap: { xs: "anywhere", sm: "normal" },
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {statusSummary.label}
                    <Box component="span" sx={{ display: { xs: "block", sm: "none" }, maxWidth: "100%", mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
                      {Number(day.completed || 0)}
                    </Box>
                  </Box>
                ) : (
                  <Stack
                    title={statusSummary.title}
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 0.35 : 0.55}
                    sx={{
                      width: "100%",
                      mt: { xs: 0.65, md: 1 },
                      minWidth: 0,
                    }}
                  >
                    {visibleItems.map((item) => (
                      <Box
                        key={item.key}
                        sx={{
                          flex: 1,
                          width: "100%",
                          minWidth: 0,
                          px: { xs: 0, sm: 0.65, md: 0.45 },
                          py: { xs: 0.35, md: 0.7 },
                          minHeight: { xs: 22, md: 34 },
                          borderRadius: { xs: "4px", sm: "999px" },
                          bgcolor: item.count > 0 ? item.bg : "#f8fafc",
                          border: "1px solid",
                          borderColor: item.count > 0 ? item.border : "#e2e8f0",
                          color: item.count > 0 ? item.color : "#94a3b8",
                          fontSize: { xs: 10, sm: 10.75, md: 11.5, xl: 12 },
                          fontWeight: 950,
                          lineHeight: 1.15,
                          textAlign: "center",
                          whiteSpace: { xs: "normal", sm: "nowrap" },
                          overflow: { xs: "visible", sm: "hidden" },
                          overflowWrap: { xs: "anywhere", sm: "normal" },
                          textOverflow: { xs: "clip", sm: "ellipsis" },
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.25,
                        }}
                      >
                        <Box component="span" sx={{ maxWidth: "100%" }}>{item.label}</Box>
                        <Box component="span" sx={{ maxWidth: "100%", fontSize: { xs: 12, sm: "inherit" }, fontVariantNumeric: "tabular-nums" }}>{item.count}</Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

export function TbmManagement() {
  const [searchParams] = useSearchParams();
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const requestedDate = searchParams.get("date");
  const [selectedDate, setSelectedDate] = useState(
    /^\d{4}-\d{2}-\d{2}$/.test(requestedDate || "") ? requestedDate : getToday()
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm(getToday()));
  const [photoFile, setPhotoFile] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const detailRequest = useRef(0);
  useEffect(() => () => { detailRequest.current += 1; }, []);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  const monthBounds = useMemo(
    () => getMonthBounds(selectedDate),
    [selectedDate]
  );

  const optionsQuery = useQuery({
    queryKey: ["risk-tbm-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/tbm/options");
      return (
        response.data || {
          employees: [],
          departments: [],
          checklistTemplates: [],
        }
      );
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [], departments: [], checklistTemplates: [] },
  });

  const sessionsQuery = useQuery({
    queryKey: ["risk-tbm-sessions", group_code, project_code, selectedDate],
    queryFn: async () => {
      const response = await api.get("/tbm/sessions", {
        params: { date: selectedDate },
      });
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: Boolean(group_code && project_code && selectedDate),
    placeholderData: [],
  });

  const monthCalendarQuery = useQuery({
    queryKey: [
      "risk-tbm-calendar",
      group_code,
      project_code,
      monthBounds.start,
      monthBounds.end,
    ],
    queryFn: async () => {
      const response = await api.get("/tbm/calendar", {
        params: {
          start: monthBounds.start,
          end: monthBounds.end,
        },
      });
      return Array.isArray(response.data)
        ? response.data
        : response.data?.days || [];
    },
    enabled: Boolean(
      group_code && project_code && monthBounds.start && monthBounds.end
    ),
    placeholderData: [],
  });

  const employees = useMemo(
    () => optionsQuery.data?.employees || [],
    [optionsQuery.data]
  );
  const departments = useMemo(
    () => optionsQuery.data?.departments || [],
    [optionsQuery.data]
  );
  const checklistTemplates = useMemo(
    () => optionsQuery.data?.checklistTemplates || [],
    [optionsQuery.data]
  );
  const sessions = useMemo(
    () => sessionsQuery.data || [],
    [sessionsQuery.data]
  );

  const invalidateSessions = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-tbm-sessions", group_code, project_code],
    });
    queryClient.invalidateQueries({
      queryKey: ["risk-tbm-calendar", group_code, project_code],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormData(form, photoFile);
      if (dialogMode === "edit") {
        return api.put(`/tbm/sessions/${editingId}`, payload);
      }
      return api.post("/tbm/sessions", payload);
    },
    onSuccess: () => {
      toast.success(
        dialogMode === "edit"
          ? "TBM 기록이 저장되었습니다."
          : "TBM 기록이 등록되었습니다."
      );
      closeDialog();
      invalidateSessions();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "TBM 저장 중 오류가 발생했습니다."
      );
    },
  });

  const summary = useMemo(() => {
    const rows = sessions;
    const totalTargets = rows.reduce(
      (sum, row) => sum + Number(row.total_members || 0),
      0
    );
    const totalAttended = rows.reduce(
      (sum, row) => sum + Number(row.attended_count || 0),
      0
    );
    const completed = rows.filter((row) => row.status === "completed").length;
    const missed = rows.filter((row) => row.status === "missed").length;
    const unregistered = rows.filter(
      (row) => row.status === "unregistered"
    ).length;
    const rate = totalTargets
      ? Math.round((totalAttended / totalTargets) * 100)
      : 0;
    return {
      total: rows.length,
      totalTargets,
      totalAttended,
      completed,
      missed,
      unregistered,
      rate,
    };
  }, [sessions]);

  const dailyTbmStatus = useMemo(() => {
    if (sessionsQuery.isLoading) return null;
    if (summary.missed > 0) {
      return {
        severity: "error",
        title: "팀별 TBM 미실시",
        message: `${summary.missed}개 팀의 TBM 미실시 상태를 확인해야 합니다.`,
      };
    }
    if (summary.unregistered > 0) {
      return {
        severity: "warning",
        title: "팀별 TBM 미등록",
        message: `${summary.unregistered}개 팀의 TBM 기록이 아직 등록되지 않았습니다.`,
      };
    }
    if (summary.completed > 0) {
      return {
        severity: "success",
        title: "팀별 TBM 완료",
        message: "선택한 날짜의 대상 팀 TBM이 완료되었습니다.",
      };
    }
    return {
      severity: "info",
      title: "팀별 TBM 확인",
      message: "조회할 팀을 선택하거나 TBM 기록을 등록해주세요.",
    };
  }, [
    sessionsQuery.isLoading,
    summary.completed,
    summary.missed,
    summary.unregistered,
  ]);

  const autopilotSteps = TBM_AUTOPILOT_STEPS;

  const openAutopilot = () => {
    setAutopilotStepIndex(0);
    setAutopilotOpen(true);
  };

  const openCreateDialog = (team = null) => {
    detailRequest.current += 1;
    setIsLoadingDetail(false);
    const targetTeam = team;
    const nextForm = targetTeam
      ? buildTeamCreateForm(selectedDate, optionsQuery.data, targetTeam)
      : makeEmptyForm(selectedDate, optionsQuery.data);
    setDialogMode("create");
    setEditingId(null);
    setForm(nextForm);
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    detailRequest.current += 1;
    setDialogOpen(false);
    setEditingId(null);
    setPhotoFile(null);
    setForm(makeEmptyForm(selectedDate, optionsQuery.data));
    setIsLoadingDetail(false);
  };

  const openEditDialog = async (session) => {
    const requestId = ++detailRequest.current;
    setDialogMode("edit");
    setEditingId(session.id);
    setPhotoFile(null);
    setIsLoadingDetail(true);
    setDialogOpen(true);
    try {
      const response = await api.get(`/tbm/sessions/${session.id}`);
      if (requestId !== detailRequest.current) return;
      if (!response.data?.id) throw new Error("TBM 기록을 찾을 수 없습니다.");
      setForm(buildEditForm(response.data, optionsQuery.data, selectedDate));
    } catch (error) {
      if (requestId !== detailRequest.current) return;
      closeDialog();
      toast.error(error?.response?.data?.message || "TBM 기록을 불러오지 못했습니다.");
    } finally {
      if (requestId === detailRequest.current) setIsLoadingDetail(false);
    }
  };

  const visibleSessions = sessions.filter((session) => statusFilter === "all" || session.status === statusFilter);

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (isLoadingDetail || saveMutation.isPending) return;
    if (!String(form.team_name || "").trim()) {
      toast.warning("팀/라인을 입력해주세요.");
      return;
    }
    if (!form.leader_emp_id) {
      toast.warning("진행자를 선택해주세요.");
      return;
    }
    if (!form.session_date) {
      toast.warning("실시 일시를 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Box className="safe-page-root"
      sx={{
        p: { xs: 1.25, sm: 1.5, lg: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom))", lg: 3 },
        height: "100%",
        overflow: "auto",
        overflowX: "hidden",
        bgcolor: "#f7f8fb",
      }}
    >
      <Stack className="safe-page-stack"
        spacing={{ xs: 1.5, lg: 2.5 }}
        sx={{ maxWidth: { xs: "100%", xl: 1540 }, mx: "auto" }}
      >
        <SafePageHeader title="작업 전 안전점검회의(TBM) 관리" data-autopilot="tbm-header" actions={<Stack
              data-autopilot="tbm-actions"
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
            >
              <TextField
                type="date"
                size="small"
                label="조회 일자"
                value={selectedDate}
                onChange={(event) => {
                  setSelectedDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={openAutopilot}
                  sx={{
                    width: 40,
                    height: 40,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    "&:hover": {
                      bgcolor: "primary.lightOpacity",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openCreateDialog()}
              >
                {isMobile ? "기록" : "등록"}
              </Button>
            </Stack>}></SafePageHeader>

        <Grid
          data-autopilot="tbm-summary"
          container
          spacing={{ xs: 1, lg: 1.5 }}
        >
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<PlaylistAddCheckIcon />}
              label="전체"
              value={`${summary.total}건`}
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<CheckCircleIcon />}
              label="완료"
              value={`${summary.completed}건`}
              color="success"
              active={statusFilter === "completed"}
              onClick={() => setStatusFilter("completed")}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<EventBusyIcon />}
              label="미실시"
              value={`${summary.missed}건`}
              color="error"
              active={statusFilter === "missed"}
              onClick={() => setStatusFilter("missed")}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<CalendarMonthIcon />}
              label="미등록"
              value={`${summary.unregistered}건`}
              color="warning"
              active={statusFilter === "unregistered"}
              onClick={() => setStatusFilter("unregistered")}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<GroupsIcon />}
              label="대상"
              value={`${summary.totalTargets}명`}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <SummaryTile
              icon={<GroupsIcon />}
              label="참석률"
              value={`${summary.rate}%`}
              color="success"
            />
          </Grid>
        </Grid>

        {sessionsQuery.isError ? (
          <Alert severity="error">
            TBM 데이터를 불러오는 중 오류가 발생했습니다.
          </Alert>
        ) : null}

        {dailyTbmStatus ? (
          <Alert
            severity={dailyTbmStatus.severity}
            sx={{ borderRadius: 2, alignItems: "center" }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              {dailyTbmStatus.title}
            </Typography>
            <Typography variant="body2">{dailyTbmStatus.message}</Typography>
          </Alert>
        ) : null}

        <MonthlyTbmCalendar
          title={monthBounds.label}
          days={monthCalendarQuery.data || []}
          selectedDate={selectedDate}
          statusFilter={statusFilter}
          isLoading={monthCalendarQuery.isLoading}
          onMonthChange={(offset) => {
            setSelectedDate(getShiftedMonthDate(selectedDate, offset));
          }}
          onSelectDate={(date) => {
            setSelectedDate(date);
          }}
          isMobile={isMobile}
        />
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>{selectedDate} 팀별 TBM · {visibleSessions.length}건</Typography>
          {sessionsQuery.isError ? (
            <Alert severity="error" action={<Button onClick={() => sessionsQuery.refetch()}>다시 조회</Button>}>
              TBM 기록을 불러오지 못했습니다.
            </Alert>
          ) : sessionsQuery.isFetching ? (
            <LinearProgress aria-label="TBM 기록 조회 중" />
          ) : (
            <TableContainer
              role="region"
              aria-label="선택 날짜 팀별 TBM 목록"
              tabIndex={0}
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Table size="small" aria-label="선택 날짜 팀별 TBM" sx={{ minWidth: 640, "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}>
                <TableHead><TableRow>
                  <TableCell>팀/라인</TableCell><TableCell>진행자</TableCell>
                  <TableCell align="center">실시 시간</TableCell><TableCell align="center">참석</TableCell>
                  <TableCell align="center">상태</TableCell><TableCell align="right">기록</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {visibleSessions.map((session) => {
                    const status = getStatusMeta(session.status);
                    return <TableRow key={session.id} hover>
                      <TableCell>{session.team_name || "-"}</TableCell>
                      <TableCell>{session.leader_name || "-"}</TableCell>
                      <TableCell align="center">{session.session_time || "-"}</TableCell>
                      <TableCell align="center">{session.attended_count || 0}/{session.total_members || 0}</TableCell>
                      <TableCell align="center"><Chip size="small" label={status.label} color={status.color} /></TableCell>
                      <TableCell align="right"><Button size="small" disabled={optionsQuery.isFetching || optionsQuery.isError || saveMutation.isPending}
                        onClick={() => session.virtual ? openCreateDialog(session) : openEditDialog(session)}>
                        {session.virtual ? "등록" : "상세/수정"}
                      </Button></TableCell>
                    </TableRow>;
                  })}
                  {!visibleSessions.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>해당 조건의 TBM 기록이 없습니다.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>

      <TbmFormDialog
        open={dialogOpen}
        mode={dialogMode}
        form={form}
        employees={employees}
        departments={departments}
        checklistTemplates={checklistTemplates}
        photoFile={photoFile}
        isMobile={isMobile}
        isSaving={saveMutation.isPending}
        isLoadingDetail={isLoadingDetail}
        onClose={closeDialog}
        onChange={handleFormChange}
        onPhotoChange={setPhotoFile}
        onSubmit={handleSubmit}
      />

      <AutoPilotGuide
        open={autopilotOpen}
        steps={autopilotSteps}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />
    </Box>
  );
}
